import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail, AlertCircle, Unlock } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authRequired, setAuthRequired] = useState(true);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("remembered_email");
    const savedPassword = localStorage.getItem("remembered_password");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // Check if auth is required
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // First check if setup is required
        const setupRes = await axios.get(`${API}/auth/setup-status`);
        if (setupRes.data.setup_required) {
          // Redirect to setup page
          navigate("/admin/setup");
          return;
        }
        
        const response = await axios.get(`${API}/auth/status`);
        setAuthRequired(response.data.auth_required);
        
        // If auth not required, go straight to admin
        if (!response.data.auth_required) {
          // Still create a token for session
          const loginResponse = await axios.post(`${API}/auth/login`, {});
          localStorage.setItem("auth_token", loginResponse.data.token);
          localStorage.setItem("auth_email", loginResponse.data.email);
          localStorage.setItem("auth_expires", loginResponse.data.expires_at);
          navigate("/admin");
        }
      } catch (err) {
        console.error("Error checking auth status:", err);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuthStatus();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      // Store token
      localStorage.setItem("auth_token", response.data.token);
      localStorage.setItem("auth_email", response.data.email);
      localStorage.setItem("auth_expires", response.data.expires_at);

      // Also login as a regular user so /account and user features work
      try {
        const userRes = await axios.post(`${API}/users/login`, {
          identifier: email,
          password
        });
        localStorage.setItem("userToken", userRes.data.token);
        localStorage.setItem("userData", JSON.stringify(userRes.data.user));
        window.dispatchEvent(new Event("storage"));
      } catch (_) {
        // User account may not exist - that's OK for admin-only users
      }

      // Handle remember me
      if (rememberMe) {
        localStorage.setItem("remembered_email", email);
        localStorage.setItem("remembered_password", password);
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_password");
      }

      // Navigate to admin
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_363f79c5-085c-4461-8ddb-f89341e71c50/artifacts/832gaaj4_ASN%20Short%20Wordmark.png" 
            alt="Antler Sports Network"
            className="h-16 w-auto mx-auto mb-4"
            style={{ background: 'transparent' }}
          />
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
            Admin Login
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            Sign in to access the dashboard
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="bg-zinc-900/50 border border-white/10 p-8">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 mb-6"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-500 text-sm">{error}</span>
            </motion.div>
          )}

          {/* Email Field */}
          <div className="mb-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="login-email"
                className="w-full bg-zinc-800 border border-white/10 text-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-white/30 placeholder-zinc-600"
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="mb-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password"
                className="w-full bg-zinc-800 border border-white/10 text-white pl-11 pr-12 py-3 text-sm focus:outline-none focus:border-white/30 placeholder-zinc-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="mb-8">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only"
                  data-testid="remember-me"
                />
                <div className={`w-5 h-5 border ${rememberMe ? 'bg-orange-500 border-orange-500' : 'bg-zinc-800 border-white/20'} flex items-center justify-center transition-colors`}>
                  {rememberMe && (
                    <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">Remember me</span>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-4 h-auto rounded-none disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                Signing in...
              </span>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-zinc-500 text-sm hover:text-white transition-colors"
          >
            ← Back to Website
          </a>
        </div>
      </motion.div>

      {/* Noise Overlay */}
      <div className="noise-overlay" aria-hidden="true" />
    </div>
  );
};

export default LoginPage;
